import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { MapPin, Database, BarChart3, Globe, ArrowRight, AlertTriangle, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useLanguage } from '@/hooks/useLanguage';
import AddressSearch from '@/components/AddressSearch';
import ProcessFlow from '@/components/ProcessFlow';
import { SEO, getOrganizationSchema, getWebSiteSchema } from '@/hooks/useSEO';

export default function HomePage() {
  const { t } = useTranslation();
  const { getLocalizedPath } = useLanguage();

  const categories = [
    {
      id: 'addresses',
      icon: MapPin,
      title: t('categories.addresses'),
      description: t('home.categoryDescriptions.addresses'),
    },
    {
      id: 'postalCodes',
      icon: Database,
      title: t('categories.postalCodes'),
      description: t('home.categoryDescriptions.postalCodes'),
    },
    {
      id: 'statistics',
      icon: BarChart3,
      title: t('categories.statistics'),
      description: t('home.categoryDescriptions.statistics'),
    },
    {
      id: 'geodata',
      icon: Globe,
      title: t('categories.geodata'),
      description: t('home.categoryDescriptions.geodata'),
    },
  ];

  return (
    <div className="flex flex-col">
      <SEO
        title={t('hero.title')}
        description={t('hero.subtitle')}
        path=""
        jsonLd={[getOrganizationSchema(), getWebSiteSchema()]}
      />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-primary/10 via-background to-secondary/5 py-10 md:py-16 lg:py-24" aria-labelledby="hero-title">
        <div className="container">
          <div className="max-w-3xl mx-auto text-center space-y-4 md:space-y-6">
            <h1 id="hero-title" className="text-2xl md:text-4xl lg:text-5xl font-bold tracking-tight text-foreground">
              {t('hero.title')}
            </h1>
            <p className="text-base md:text-lg text-muted-foreground max-w-2xl mx-auto">
              {t('hero.subtitle')}
            </p>
            <div className="flex flex-wrap items-center justify-center gap-3 mt-2">
              <Link to={getLocalizedPath('/shop')}>
                <Button size="default" className="bg-primary hover:bg-primary/90 text-primary-foreground md:text-base">
                  {t('shop.title')}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
              <Link to={getLocalizedPath('/kontakt')}>
                <Button size="default" variant="outline" className="border-primary/30 text-foreground hover:bg-primary/10 md:text-base">
                  {t('common.contact')}
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Address Search Section */}
      <section className="py-8 md:py-10 lg:py-14 bg-muted/20" aria-labelledby="address-search-title">
        <div className="container">
          <div className="max-w-5xl mx-auto space-y-4 md:space-y-5">
            <h2 id="address-search-title" className="text-xl md:text-2xl lg:text-3xl font-bold text-center text-foreground">
              {t('addressSearch.title')}
            </h2>
            <AddressSearch />
          </div>
        </div>
      </section>

      {/* Address Data Importance Section */}
      <AddressDataSection />

      {/* Process Flow Section */}
      <ProcessFlow />

      {/* Categories Section */}
      <CategoriesSection categories={categories} getLocalizedPath={getLocalizedPath} />

      {/* Why Choose Us Section */}
      <WhyChooseUsSection getLocalizedPath={getLocalizedPath} />

      {/* Postal Codes Link Section */}
      <PostalCodesLinkSection getLocalizedPath={getLocalizedPath} />

      {/* Contact CTA */}
      <ContactCtaSection getLocalizedPath={getLocalizedPath} />
    </div>
  );
}

function AddressDataSection() {
  const { t } = useTranslation();

  return (
    <section className="py-10 md:py-16 lg:py-24 bg-background" aria-labelledby="address-data-title">
      <div className="container">
        <div className="max-w-4xl mx-auto">
          <h2 id="address-data-title" className="text-2xl md:text-3xl font-bold mb-3 md:mb-4 text-foreground">
            {t('home.addressData.title')}
          </h2>
          <p className="text-muted-foreground text-base md:text-lg mb-6 md:mb-10 leading-relaxed">
            {t('home.addressData.intro')}
          </p>

          <div className="grid md:grid-cols-2 gap-4 md:gap-8 mb-6 md:mb-10">
            <Card className="border-border/50">
              <CardHeader className="pb-3">
                <div className="flex items-center gap-3 mb-2">
                  <div className="h-10 w-10 rounded-lg bg-destructive/10 flex items-center justify-center flex-shrink-0">
                    <AlertTriangle className="h-5 w-5 text-destructive" />
                  </div>
                  <CardTitle className="text-lg">{t('home.addressData.causesTitle')}</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3">
                  {(t('home.addressData.causes', { returnObjects: true }) as string[]).map((cause, index) => (
                    <li key={index} className="flex items-start gap-2 text-muted-foreground text-sm">
                      <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-destructive/60 flex-shrink-0" />
                      {cause}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            <Card className="border-border/50">
              <CardHeader className="pb-3">
                <div className="flex items-center gap-3 mb-2">
                  <div className="h-10 w-10 rounded-lg bg-secondary/10 flex items-center justify-center flex-shrink-0">
                    <CheckCircle className="h-5 w-5 text-secondary" />
                  </div>
                  <CardTitle className="text-lg">{t('home.addressData.consequencesTitle')}</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3">
                  {(t('home.addressData.consequences', { returnObjects: true }) as string[]).map((consequence, index) => (
                    <li key={index} className="flex items-start gap-2 text-muted-foreground text-sm">
                      <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-secondary/60 flex-shrink-0" />
                      {consequence}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>

          <p className="text-muted-foreground leading-relaxed text-base">
            {t('home.addressData.conclusion')}
          </p>
        </div>
      </div>
    </section>
  );
}

interface CategoriesSectionProps {
  categories: Array<{
    id: string;
    icon: React.ComponentType<{ className?: string }>;
    title: string;
    description: string;
  }>;
  getLocalizedPath: (path: string) => string;
}

function CategoriesSection({ categories, getLocalizedPath }: CategoriesSectionProps) {
  const { t } = useTranslation();

  return (
    <section className="py-10 md:py-16 lg:py-24 bg-muted/30" aria-labelledby="categories-title">
      <div className="container">
        <div className="text-center mb-8 md:mb-12">
          <h2 id="categories-title" className="text-2xl md:text-3xl font-bold mb-3 md:mb-4">{t('common.dataFiles')}</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto text-sm md:text-base">
              {t('home.categoriesSubtitle')}
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-6">
          {categories.map((category) => (
          <Link key={category.id} to={getLocalizedPath(`/datafiler?category=${category.id}`)}>
              <Card className="group hover:shadow-lg transition-shadow cursor-pointer border-border/50 hover:border-secondary/50 h-full">
                <CardHeader className="p-4 md:p-6">
                  <div className="h-10 w-10 md:h-12 md:w-12 rounded-lg bg-secondary/10 flex items-center justify-center mb-3 md:mb-4 group-hover:bg-secondary/20 transition-colors">
                    <category.icon className="h-5 w-5 md:h-6 md:w-6 text-secondary" />
                  </div>
                  <CardTitle className="text-sm md:text-lg leading-tight">{category.title}</CardTitle>
                  <CardDescription className="text-xs md:text-sm hidden md:block">{category.description}</CardDescription>
                </CardHeader>
              </Card>
            </Link>
          ))}
        </div>

        <div className="mt-6 md:mt-10 text-center">
          <Link to={getLocalizedPath('/shop')}>
            <Button size="default" className="bg-primary hover:bg-primary/90 text-primary-foreground md:text-base md:px-6 md:py-3">
              {t('shop.title')}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
}

function WhyChooseUsSection({ getLocalizedPath }: { getLocalizedPath: (path: string) => string }) {
  const { t } = useTranslation();

  return (
    <section className="py-10 md:py-16 lg:py-24" aria-labelledby="why-choose-title">
      <div className="container">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          <div className="space-y-4 md:space-y-6">
            <h2 id="why-choose-title" className="text-2xl md:text-3xl font-bold">{t('home.whyChoose.title')}</h2>
            <div className="space-y-3 md:space-y-4">
              <div className="flex gap-3">
                <div className="h-7 w-7 md:h-8 md:w-8 rounded-full bg-secondary/10 flex items-center justify-center flex-shrink-0">
                  <span className="text-secondary font-bold text-xs md:text-sm">1</span>
                </div>
                <div>
                  <h3 className="font-semibold mb-0.5 text-sm md:text-base">{t('home.whyChoose.quality')}</h3>
                  <p className="text-muted-foreground text-xs md:text-sm">
                    {t('home.whyChoose.qualityDesc')}
                  </p>
                </div>
              </div>
              <div className="flex gap-3">
                <div className="h-7 w-7 md:h-8 md:w-8 rounded-full bg-secondary/10 flex items-center justify-center flex-shrink-0">
                  <span className="text-secondary font-bold text-xs md:text-sm">2</span>
                </div>
                <div>
                  <h3 className="font-semibold mb-0.5 text-sm md:text-base">{t('home.whyChoose.delivery')}</h3>
                  <p className="text-muted-foreground text-xs md:text-sm">
                    {t('home.whyChoose.deliveryDesc')}
                  </p>
                </div>
              </div>
              <div className="flex gap-3">
                <div className="h-7 w-7 md:h-8 md:w-8 rounded-full bg-secondary/10 flex items-center justify-center flex-shrink-0">
                  <span className="text-secondary font-bold text-xs md:text-sm">3</span>
                </div>
                <div>
                  <h3 className="font-semibold mb-0.5 text-sm md:text-base">{t('home.whyChoose.coverage')}</h3>
                  <p className="text-muted-foreground text-xs md:text-sm">
                    {t('home.whyChoose.coverageDesc')}
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div className="bg-gradient-to-br from-primary/20 to-secondary/20 rounded-2xl p-6 md:p-8 lg:p-12">
            <div className="text-center space-y-3 md:space-y-4">
              <h3 className="text-xl md:text-2xl font-bold">{t('home.whyChoose.ctaTitle')}</h3>
              <p className="text-muted-foreground text-sm md:text-base">
                {t('home.whyChoose.ctaDesc')}
              </p>
              <Link to={getLocalizedPath('/shop')}>
                <Button size="default" className="bg-secondary hover:bg-secondary/90 text-secondary-foreground mt-3 md:mt-4 md:text-base md:px-6">
                  {t('home.whyChoose.ctaButton')}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function PostalCodesLinkSection({ getLocalizedPath }: { getLocalizedPath: (path: string) => string }) {
  const { t } = useTranslation();

  return (
    <section className="py-10 md:py-16 bg-muted/30" aria-labelledby="postal-codes-title">
      <div className="container">
        <div className="max-w-3xl mx-auto text-center space-y-4">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-secondary/10">
            <MapPin className="w-6 h-6 text-secondary" />
          </div>
          <h2 id="postal-codes-title" className="text-2xl md:text-3xl font-bold">{t('home.postalSection.title')}</h2>
          <p className="text-muted-foreground">
            {t('home.postalSection.description')}
          </p>
          <Link to={getLocalizedPath('/information/postnummer')}>
            <Button variant="outline" size="lg" className="mt-2">
              {t('home.postalSection.button')}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
}

function ContactCtaSection({ getLocalizedPath }: { getLocalizedPath: (path: string) => string }) {
  const { t } = useTranslation();

  return (
    <section className="py-10 md:py-16 bg-sidebar text-sidebar-foreground" aria-labelledby="contact-cta-title">
      <div className="container text-center">
        <h2 id="contact-cta-title" className="text-xl md:text-2xl font-bold mb-3 md:mb-4">{t('home.contactCta.title')}</h2>
        <p className="text-sidebar-foreground/80 mb-4 md:mb-6 max-w-xl mx-auto text-sm md:text-base">
          {t('home.contactCta.description')}
        </p>
        <Link to={getLocalizedPath('/kontakt')}>
          <Button size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90">
            {t('common.contact')}
          </Button>
        </Link>
      </div>
    </section>
  );
}
